// ============================================================================
// AUTH CONTEXT - Global Authentication State Management
// ============================================================================

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import {
  login as authServiceLogin,
  logout as authServiceLogout,
  getIdToken,
  getCurrentAuthenticatedUser,
  fetchUserProfile,
  UserProfile
} from '../services/authService';
import { setAccessToken as setApiAccessToken, setAuthFunctions, api } from '../services/api';

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  authError: string | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasFeature: (feature: string) => boolean;
  updateNavPreferences: (pinnedItems: string[]) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  /**
   * Check current authentication status with retry logic
   */
  const checkAuthStatus = async (retries = 3) => {
    setLoading(true);
    setAuthError(null);

    try {
      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          // Try to get the current user
          const currentUser = await getCurrentAuthenticatedUser();

          if (currentUser) {
            const token = await getIdToken();

            if (token) {
              setAccessToken(token);

              // Fetch full user profile from backend
              const profile = await fetchUserProfile(token);

              if (profile) {
                setUser(profile);
                return; // Success!
              } else {
                console.error('Failed to fetch user profile from backend');
                // Don't logout, just keep Cognito session
                setUser(null);
              }
            } else {
              console.error('No ID token available');
            }
          } else {
            // No user, stop retrying
            setUser(null);
            setAccessToken(null);
            return;
          }
        } catch (error: any) {
          console.error(`Auth status check attempt ${attempt + 1}/${retries}:`, error.message);

          // If this is not the last attempt, wait before retrying
          if (attempt < retries - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }

          // All retries failed
          setUser(null);
          setAccessToken(null);
          setAuthError('Authentication failed. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initiate login flow
   */
  const login = useCallback(async () => {
    try {
      const currentPath = window.location.pathname;
      const authPages = ['/login', '/auth/callback', '/unauthorized'];
      if (!authPages.includes(currentPath)) {
        sessionStorage.setItem('auth_redirect', currentPath);
      }
      await authServiceLogin();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, []);

  /**
   * Handle logout
   */
  const handleLogout = useCallback(async () => {
    try {
      setUser(null);
      setAccessToken(null);
      await authServiceLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, []);

  /**
   * Refresh access token
   */
  const refreshToken = useCallback(async () => {
    try {
      const token = await getIdToken();
      if (token) {
        setAccessToken(token);

        // Also refresh user profile if needed
        if (!user) {
          const profile = await fetchUserProfile(token);
          if (profile) {
            setUser(profile);
          }
        }
      } else {
        // If token refresh fails, logout
        await handleLogout();
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      await handleLogout();
    }
  }, [user, handleLogout]);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      // Check if we're on the callback page
      const isCallback = window.location.pathname === '/auth/callback';

      if (isCallback) {
        // Poll for auth readiness: check every 300ms up to 10 seconds
        const maxAttempts = 33; // ~10 seconds
        for (let i = 0; i < maxAttempts; i++) {
          try {
            const currentUser = await getCurrentAuthenticatedUser();
            if (currentUser) break;
          } catch {
            // Not ready yet
          }
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } else {
        // Small delay for other pages
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      await checkAuthStatus();
      setInitialized(true);
    };

    initAuth();
  }, []);

  // Sync access token with API service whenever it changes
  useEffect(() => {
    setApiAccessToken(accessToken);
  }, [accessToken]);

  // Check auth status periodically after initialization (every 5 minutes)
  useEffect(() => {
    if (!initialized) return;

    const interval = setInterval(() => {
      if (accessToken) {
        refreshToken();
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [initialized, accessToken, refreshToken]);

  // Auto-refresh token before expiration
  useEffect(() => {
    if (!accessToken) return;

    try {
      // Decode JWT to get expiration
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const refreshAt = expiresAt - (5 * 60 * 1000); // 5 min before expiration
      const timeout = refreshAt - Date.now();

      if (timeout > 0) {
        const timer = setTimeout(() => {
          refreshToken();
        }, timeout);
        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.error('Error parsing token:', error);
    }
  }, [accessToken, refreshToken]);

  /**
   * Refresh user profile from backend
   */
  const refreshUser = useCallback(async () => {
    try {
      if (accessToken) {
        const profile = await fetchUserProfile(accessToken);
        if (profile) {
          setUser(profile);
        }
      }
    } catch (error) {
      console.error('User profile refresh error:', error);
    }
  }, [accessToken]);

  const hasRole = useCallback((role: string): boolean => {
    return user?.role === role;
  }, [user]);

  const hasFeature = useCallback((feature: string): boolean => {
    return user?.enabledFeatures?.includes(feature) ?? false;
  }, [user]);

  const updateNavPreferences = useCallback(async (pinnedItems: string[]): Promise<void> => {
    const previous = user?.navPreferences;
    setUser(prev => prev ? { ...prev, navPreferences: { pinnedItems } } : prev);
    try {
      await api.patch('/api/users/me/preferences', { pinnedItems });
    } catch (error) {
      setUser(prev => prev ? { ...prev, navPreferences: previous } : prev);
      console.error('Failed to save nav preferences:', error);
    }
  }, [user]);

  // Setup auth functions in API service
  useEffect(() => {
    setAuthFunctions(refreshToken, handleLogout);
  }, [refreshToken, handleLogout]);

  const value = useMemo((): AuthContextType => ({
    user,
    loading,
    authError,
    isAuthenticated: !!user,
    accessToken,
    login,
    logout: handleLogout,
    refreshToken,
    refreshUser,
    hasRole,
    hasFeature,
    updateNavPreferences,
  }), [user, loading, authError, accessToken, login, handleLogout, refreshToken, refreshUser, hasRole, hasFeature, updateNavPreferences]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
